import i18n from '@/plugins/i18n'
import store from '@/store'

export const colors = [
  '#D13D4B',
  '#DD644C',
  '#EB8547',
  '#F4A641',
  '#FFC73C',
  '#F9E03B',
  '#F9E03B',
  '#BBD665',
  '#9FD17C',
  '#86CC8E'
]

function getColorForValue(value: number, max: number, reverse = false, colorRange: string[] = colors) {
  let percentage = value / max

  if (percentage > 1) percentage = 1

  let index = Math.floor(percentage * 10)

  if (index === 0) index = 1

  if (reverse) {
    return colorRange[colorRange.length - index]
  }

  return colorRange[index - 1]
}

interface ColorValueDefinition {
  max: number,
  color: string
}

function getColorForValueWithDefinition(value: number, definition: ColorValueDefinition[]) {
  if (!value) value = 0

  return definition.find(({ max }) => value <= max)?.color
}

function processMaxValues(stats: any[], factor: number = 1.3) {
  if (!stats) return {}

  const maxValues: any = {}

  for (const stat of stats) {
    if (!stat) continue

    const keys = Object.keys(stat)

    for (const key of keys) {
      maxValues[key] = (!maxValues[key] || stat[key] * factor > maxValues[key]) ? stat[key] * factor : maxValues[key]
    }
  }

  return maxValues
}

function processMinValues(stats: any[]) {
  if (!stats) return {}

  const minValues: any = {}

  for (const stat of stats) {
    if (!stat) continue

    const keys = Object.keys(stat)

    for (const key of keys) {
      minValues[key] = (!minValues[key] || stat[key] < minValues[key]) ? stat[key] : minValues[key]
    }
  }

  return minValues
}

function getRandomInt(min: number, max: number): number {
  min = Math.ceil(min)
  max = Math.floor(max)
  return Math.floor(Math.random() * (max - min) + min)
}

function toCamelCase(str: string) {
  return str
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (match, chr) => chr.toUpperCase())
}

function getOrdinal(num: number) {
  if (num % 100 >= 11 && num % 100 <= 13) {
    return i18n.t('ordinal.1', { num: num })
  }

  switch (num % 10) {
    case 1:
      return i18n.t('ordinal.1', { num: num })
    case 2:
      return i18n.t('ordinal.2', { num: num })
    case 3:
      return i18n.t('ordinal.3', { num: num })
    default:
      return i18n.t('ordinal.other', { num: num })
  }
}

function calculateDistribution(
  players: any[],
  statVariable: string,
  min: number = 1,
  max: number = 100,
  step: number = 10,
  extraBinForMax: boolean = true
) {
  const numBins = Math.ceil((max - min) / step)

  const bins = Array(numBins).fill(0).map((_, index) => {
    const binStart = min + (index * step)
    const binEnd = Math.min(binStart + step - 1, max - 1) // Ensure last bin doesn't overlap with max
    return {
      key: step > 1 ? `${binStart}-${binEnd}` : `${binStart}`,
      value: 0
    }
  })

  if (extraBinForMax) {
    bins.push({ key: `${max}+`, value: 0 })
  }

  for (const player of players) {
    if (!player.player_stats || typeof player.player_stats[statVariable] !== 'number') {
      continue
    }

    const value = player.player_stats[statVariable]

    if (extraBinForMax && value >= max) {
      bins[bins.length - 1].value++
      continue
    }

    if (value >= min && value < max) {
      const binIndex = Math.floor((value - min) / step)
      if (binIndex >= 0 && binIndex < numBins) {
        bins[binIndex].value++
      }
    }
  }

  return bins
}

export function calculateDistributionFromCounts(
  dist: Array<{ value: number | string; count: number | string }>,
  min: number = 1,
  max: number = 10,
  step: number = 1
) {
  const map = new Map<number, number>()
  for (const item of dist) {
    const k = Number((item as any).value)
    const v = Number((item as any).count) || 0
    if (Number.isFinite(k)) {
      // accumulate in case of duplicates
      map.set(k, (map.get(k) || 0) + v)
    }
  }

  // When step = 1, preserve original behavior: each integer key in [min, max]
  if (step <= 1) {
    const full: Array<{ key: number; value: number }> = []
    for (let g = min; g <= max; g++) {
      full.push({ key: g, value: map.get(g) || 0 })
    }
    return full
  }

  // When step > 1, aggregate into ranges and output range labels as keys, e.g. "1-10"
  const ranged: Array<{ key: string; value: number }> = []
  for (let start = min; start <= max; start += step) {
    const end = Math.min(start + step - 1, max)
    let sum = 0
    for (let val = start; val <= end; val++) {
      sum += map.get(val) || 0
    }
    ranged.push({ key: `${start}-${end}`, value: sum })
  }
  return ranged
}

function splitVariable(variable: string) {
  const index = variable.lastIndexOf('_')

  if (index === -1) {
    return [variable, '']
  }

  return [
    variable.substring(0, index),
    variable.substring(index + 1)
  ]
}

function adjustGridForTableHeights(
  grid: any[] = [],
  tableRowCount: number = 0,
  diagnosticTableRowCount: number = 0,
  rowHeightPx: number = 52,
  headerPx: number = 130
) {
  if (!grid || !Array.isArray(grid)) return []

  const heightDiffs = new Map<any, number>()
  for (const el of grid) {
    if (el.type === 'table' && tableRowCount > 0) {
      const itemsLength = tableRowCount || 5
      const predictedHeight = Math.ceil((itemsLength * rowHeightPx + headerPx) / 80)
      heightDiffs.set(el, predictedHeight - el.h)
    } else if (el.type === 'diagnostic_table' && diagnosticTableRowCount > 0) {
      const itemsLength = diagnosticTableRowCount || 5
      const predictedHeight = Math.ceil((itemsLength * 52 + 130) / 80)
      heightDiffs.set(el, predictedHeight - el.h)
    } else if (el.type === 'diagnostic_latest_table' && tableRowCount > 0) {
      const itemsLength = tableRowCount || 5
      const predictedHeight = Math.ceil((itemsLength * 52 + 130) / 80)
      heightDiffs.set(el, predictedHeight - el.h)
    }
  }

  return grid.map(element => {
    const result = { ...element }

    let yOffset = 0
    for (const [other, diff] of heightDiffs) {
      if (other !== element && other.y < element.y) {
        yOffset += diff
      }
    }
    result.y = element.y + yOffset

    const ownDiff = heightDiffs.get(element)
    if (ownDiff) result.h = element.h + ownDiff

    return result
  })
}

const dateTimeFormatMap: { [key: string]: string } = {
  'HH:mm': 'h:mm A',
  'HH:mm:ss': 'h:mm:ss A',
  'ddd, DD/MM/YYYY - HH:mm': 'ddd, MM/DD/YYYY - h:mm A',
  'ddd, D MMM, HH:mm': 'ddd, MMM D, h:mm A',
  'ddd, D MMMM, HH:mm': 'ddd, MMMM D, h:mm A',
  'ddd, D MMM HH:mm:ss': 'ddd, MMM D h:mm:ss A',
  'dddd, D MMM, YYYY HH:mm:ss': 'dddd, MMM D, YYYY h:mm:ss A',
  'dddd, D MMMM HH:mm': 'dddd, MMMM D h:mm A',
  'dddd, D MMMM HH:mm:ss': 'dddd, MMMM D h:mm:ss A',
  'dddd, D MMMM, HH:mm:ss': 'dddd, MMMM D, h:mm:ss A',
  'DD/MM/YYYY HH:mm': 'MM/DD/YYYY h:mm A',
  'DD/MM/YYYY HH:mm:ss': 'MM/DD/YYYY h:mm:ss A',
  'D MMMM, YYYY, HH:mm:ss': 'MMMM D, YYYY, h:mm:ss A',
  'D MMM, YYYY HH:mm': 'MMM D, YYYY h:mm A',
  'D MMM, YYYY HH:mm:ss': 'MMM D, YYYY h:mm:ss A',
  'D MMM, YYYY, HH:mm': 'MMM D, YYYY, h:mm A',
  'D MMM, YYYY, HH:mm:ss': 'MMM D, YYYY, h:mm:ss A',
  'ddd, D MMM, YYYY HH:mm:ss': 'ddd, MMM D, YYYY h:mm:ss A',
  'DD MMM YYYY, HH:mm': 'MMM D, YYYY, h:mm A',
  'DD/MM': 'MM/DD',
  'D. MMM, YYYY': 'MMM D, YYYY',
  'D. MMM': 'MMM D',
  'dd MMM': 'MMM dd',
  'ddd, dd. MMM': 'ddd, MMM dd',
  'ddd, DD MMM': 'ddd, MMM D',
  'ddd, DD.MM.': 'ddd, MM.DD.',
  'dd.MM.yyyy.': 'MM/DD/YYYY',
  'DD/MM/YYYY': 'MM/DD/YYYY',
  'D/M/YYYY': 'M/D/YYYY',
  'YYYY-DD-MM': 'YYYY-MM-DD',
  'DD.MM.YYYY.': 'MM/DD/YYYY',
  'DD.MM.YYYY': 'MM/DD/YYYY',
  'ddd, D MMM, YYYY': 'ddd, MMM D, YYYY',
  'dddd, D MMM, YYYY': 'dddd, MMM D, YYYY',
  'D MMM, YYYY - ddd': 'MMM D, YYYY - ddd',
  'DD/MM/YYYY - ddd': 'MM/DD/YYYY - ddd',
  'dd/MM/yy': 'MM/dd/yy',
  'D MMM, YYYY - [today]': 'MMM D, YYYY - [today]',
  'D MMM': 'MMM D',
  'd MMM': 'MMM d',
  'D MMM YYYY': 'MMM D, YYYY',
  'D MMM, YYYY': 'MMM D, YYYY',
  'DD MMM, YYYY': 'MMM D, YYYY',
  'dd MMM, YYYY': 'MMM d, YYYY',
  'ddd, DD/MM/YYYY': 'ddd, MM/DD/YYYY',
  'dddd, D MMMM': 'dddd, MMMM D'
}

function getDateTimeFormatForUser(originalFormat: string) {
  const user = store.getters.authUser

  if (user.organisation_settings?.unit_system !== 'imperial') return originalFormat

  return dateTimeFormatMap[originalFormat] || originalFormat
}

function hasSixZoneSetup() {
  return store.getters.authUser?.organisation_settings?.has_six_zone_setup === true
}

const unitMap: { [key: string]: string } = {
  'm/s²': 'ft/s²',
  'km/h': 'mph',
  'm/min': 'ft/min',
  'm/s': 'ft/s'
}

function getUnitSuffixForUser(originalUnit: string) {
  const user = store.getters.authUser

  if (user.organisation_settings?.unit_system !== 'imperial') return originalUnit

  return unitMap[originalUnit] || originalUnit
}

function convertSpeed(value: number, originalUnit: string) {
  let result

  if (originalUnit === 'm/s') result = value * 3.6
  if (originalUnit === 'ft/s') result = value * 3.28 * 0.68 // first m/s to ft/s, then ft/s to mph
  if (originalUnit === 'km/h') result = value / 3.6
  if (originalUnit === 'mph') result = (value / 0.68) / 3.28 // first mph to ft/s, then ft/s to m/s

  if (!result) return value

  return result.toFixed(1)
}

function convertAccelerationToImperial(value: number, reversed: boolean = false) {
  const user = store.getters.authUser

  if (user.organisation_settings?.unit_system !== 'imperial') return value

  if (reversed) return (value / 3.28).toFixed(1)

  return (value * 3.28).toFixed(1)
}

function getVariableNameWithAggregation(variable: string) {
  const VALID_AGGREGATIONS = ['DL', 'WL', 'WC', 'AL', 'CL', 'ACWR', 'FRES', 'MON', 'STR']

  const parts = variable.split('_')

  if (parts.length < 2) {
    return i18n.t(`variables.${variable}`)
  }

  const potentialAggregation = parts[parts.length - 1]

  if (VALID_AGGREGATIONS.includes(potentialAggregation)) {
    const varKey = parts.slice(0, -1).join('_')
    return `${i18n.t(`variables.${varKey}`)}: ${i18n.t(`variables.${potentialAggregation}.long`)}`
  }

  return i18n.t(`variables.${variable}`)
}

function getTranslatedVariableName(variableKey: string, useLong: boolean = true) {
  const suffix = useLong ? '.long' : ''
  return i18n.t(`variables.${variableKey}${suffix}`)
}

const EXERCISE_GROUP_NAMES: Record<string, string> = {
  STGH: 'strength',
  FTB: 'football',
  SPD: 'speed',
  END: 'endurance',
  PLY: 'plyometrics',
  BAL: 'ballistics',
  CRD: 'cardio',
  MOB: 'mobility',
  BSKT: 'basketball',
  SWM: 'swimming',
  STRENGTH: 'strength',
  FOOTBALL: 'football',
  SPEED: 'speed',
  ENDURANCE: 'endurance',
  PLYOMETRICS: 'plyometrics',
  BALLISTICS: 'ballistics',
  CARDIO: 'cardio',
  MOBILITY: 'mobility',
  BASKETBALL: 'basketball',
  SWIMMING: 'swimming',
  EXERCISE: 'exercise'
}

function isExerciseCategory(category: string) {
  return category in EXERCISE_GROUP_NAMES
}

export function getLocalizedVariableText(variable: { value: string; category: string; long_text: string }): string {
  if (!variable) return ''

  if (isExerciseCategory(variable.category)) {
    const cleanCategory = EXERCISE_GROUP_NAMES[variable.category]
    const translationKey = `${cleanCategory}.insight.${variable.value}`
    const translated = String(i18n.t(translationKey))
    return translated === translationKey ? variable.long_text : translated
  }

  return variable.long_text
}

function getTranslatedTagValue(value: string) {
  if (!value) return ''

  const translateSingle = (val: string) => {
    const cleanKey = EXERCISE_GROUP_NAMES[val] || val.toLowerCase()
    const translationKey = `training_exercise_tags.values.${cleanKey}`
    if (!i18n.t(translationKey)) return val
    const translated = i18n.t(translationKey)
    return translated === translationKey ? val : translated
  }

  if (value.includes(', ')) {
    return value.split(', ').map(v => translateSingle(v)).join(', ')
  }

  return translateSingle(value)
}

export { getColorForValue, processMaxValues, processMinValues, getRandomInt, getColorForValueWithDefinition, toCamelCase, getOrdinal, calculateDistribution, splitVariable, adjustGridForTableHeights, ColorValueDefinition, getDateTimeFormatForUser, getUnitSuffixForUser, convertSpeed, convertAccelerationToImperial, getVariableNameWithAggregation, getTranslatedVariableName, getTranslatedTagValue, hasSixZoneSetup }
